import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../shared/useNotifications';
import { DISCIPLINE_TABS } from '../../utils/constants';
import '../auth/ClubRegister.css';
import './AuthShared.css';

const CoachRegister = ({ onBackToHome, onBackToLogin }) => {
  const navigate = useNavigate();
  const { signUp, signOut } = useAuth();
  const { success: showSuccess } = useNotifications();
  const [step, setStep] = useState('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    phone: '',
    location: '',
    specialties: [],
    experience_years: '',
    bio: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleSpecialty = (key) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(key)
        ? prev.specialties.filter(d => d !== key)
        : [...prev.specialties, key]
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) { setError('El nombre es obligatorio'); return false; }
    if (!formData.email.trim()) { setError('El correo es obligatorio'); return false; }
    if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email.trim())) { setError('Correo inválido'); return false; }
    if (!formData.password) { setError('La contraseña es obligatoria'); return false; }
    if (formData.password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return false; }
    if (formData.password !== formData.passwordConfirm) { setError('Las contraseñas no coinciden'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateForm()) { setLoading(false); return; }

    try {
      const { data: authDataWithProfile, error: authErrorWithProfile } = await signUp(
        formData.email.trim(),
        formData.password,
        'coach',
        {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || null,
          location: formData.location.trim() || null,
          specialties: formData.specialties.length > 0 ? formData.specialties : [],
          experience_years: formData.experience_years ? parseInt(formData.experience_years) : 0,
          bio: formData.bio.trim() || null,
          looking_for_club: false,
          is_active: true,
        }
      );

      if (authErrorWithProfile) {
        setError(authErrorWithProfile);
        setLoading(false);
        return;
      }

      const coachData = authDataWithProfile?.profile;
      if (!coachData) {
        setError('Error guardando el perfil del entrenador. Intenta nuevamente.');
        setLoading(false);
        return;
      }

      showSuccess('✅ Cuenta creada exitosamente');
      // Cerrar sesión: el entrenador debe iniciar sesión manualmente
      try { await signOut(); } catch (_) {}
      setStep('success');
    } catch (err) {
      setError('Error al procesar el registro. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="club-auth-container">
        <div className="auth-card">
          <div className="auth-success">
            <CheckCircle size={48} />
            <h2>¡Cuenta Creada!</h2>
            <p>Tu cuenta de entrenador ha sido creada exitosamente.</p>
            <p style={{ fontSize: '12px', color: '#9ca3af' }}>Ya puedes iniciar sesión y solicitar acceso a los clubes donde trabajas. Una vez que el club apruebe tu solicitud, podrás gestionar tu participación.</p>
            <button type="button" onClick={onBackToLogin} className="auth-btn-primary" style={{ marginTop: 20 }}>
              Ir al Inicio de Sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <button type="button" onClick={onBackToLogin} className="auth-back" aria-label="Volver">
          <ArrowLeft size={18} /> Volver
        </button>

        <div className="auth-header">
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="auth-logo" onClick={() => navigate('/')} />
          <h2>Registro de Entrenador</h2>
          <p>Crea tu cuenta para acceder a la plataforma</p>
        </div>

        {error && (
          <div className="auth-alert auth-alert-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-group">
            <label>Nombre Completo *</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Tu nombre completo" required disabled={loading} />
          </div>

          <div className="auth-form-group">
            <label>Correo Electrónico *</label>
            <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="entrenador@correo.com" required disabled={loading} />
          </div>

          <div className="club-form-row">
            <div className="auth-form-group">
              <label>Contraseña *</label>
              <input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="Mínimo 6 caracteres" required disabled={loading} />
            </div>
            <div className="auth-form-group">
              <label>Confirmar Contraseña *</label>
              <input type="password" name="passwordConfirm" value={formData.passwordConfirm} onChange={handleInputChange} placeholder="Repite la contraseña" required disabled={loading} />
            </div>
          </div>

          <div className="club-form-row">
            <div className="auth-form-group">
              <label>Teléfono</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+54 11 1234-5678" disabled={loading} />
            </div>
            <div className="auth-form-group">
              <label>Años de Experiencia</label>
              <input type="number" name="experience_years" value={formData.experience_years} onChange={handleInputChange} placeholder="0" min="0" max="60" disabled={loading} />
            </div>
          </div>

          <div className="auth-form-group">
            <label>Ubicación</label>
            <input type="text" name="location" value={formData.location} onChange={handleInputChange} placeholder="Ciudad, Provincia" disabled={loading} />
          </div>

          <div className="auth-form-group">
            <label>Sobre mí</label>
            <textarea name="bio" value={formData.bio} onChange={handleInputChange} placeholder="Breve descripción de tu experiencia y especialización..." disabled={loading} rows={3} style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '15px', padding: '14px 16px', borderRadius: 8, border: '1px solid #ebebeb', width: '100%', boxSizing: 'border-box', background: '#f7f7f7', outline: 'none' }} />
          </div>

          {/* Especialidades (disciplinas) */}
          <div className="auth-form-group">
            <label>Especialidades / Disciplinas</label>
            <div className="club-discipline-grid">
              {DISCIPLINE_TABS.map(d => (
                <button
                  key={d.key}
                  type="button"
                  className={`club-discipline-chip ${formData.specialties.includes(d.key) ? 'active' : ''}`}
                  onClick={() => toggleSpecialty(d.key)}
                  disabled={loading}
                >
                  {d.logo && <img src={d.logo} alt={d.fullName} style={{ width: 20, height: 20 }} />}
                  <span>{d.fullName}</span>
                </button>
              ))}
            </div>
          </div>

          <button type="submit" className="auth-btn-primary" disabled={loading}>
            {loading ? 'Registrando...' : 'Crear Cuenta'}
          </button>
        </form>

        <div className="auth-divider">
          <span>¿Ya tienes cuenta?</span>
        </div>

        <button type="button" onClick={onBackToLogin} className="auth-btn-outline" disabled={loading}>
          Iniciar Sesión
        </button>

        <p className="auth-copyright">
          © {new Date().getFullYear()} CopaGimnasta. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default CoachRegister;
